import { encountersAt, GROWN_SPLIT, SPOTS, VETERAN_ENCOUNTERS, ZONE_BEDS, ZONES, zoneAt, type EncounterRow, type ZoneId } from "./map.ts";
import { peekMove } from "./moves.ts";
import { CIRCUIT_IDS, ENCORE_IDS, TRAINERS, encoreOpen, trainerEncounter, type TrainerId } from "./trainers.ts";
import { maxHp } from "../game/mon.ts";
import type { SaveState } from "../game/save.ts";

export interface TrainingBed {
  zone: ZoneId;
  region: "all" | "north" | "south";
  lo: number;
  hi: number;
}

function band(rows: EncounterRow[]): { lo: number; hi: number } | null {
  return rows.length ? { lo: Math.min(...rows.map(r => r.lo)), hi: Math.max(...rows.map(r => r.hi)) } : null;
}

/** A bed's advertised band ignores the rare (w <= 4) sightings that only show up now and then. */
const RARE_W = 4;
function bedBand(rows: EncounterRow[]): { lo: number; hi: number } | null {
  return band(rows.filter(r => r.w > RARE_W));
}

/**
 * Every duel in the order PLAN recommends: Stubtown's routes, Vell's trial, the circuit, then the
 * Frontier, the Undernet and the Sidechain, each region picking up where the last one ends, with the
 * story's duels (Remy and the KOL Collective) threaded in at their levels, Kole last before Osric.
 */
export const ROUTE: readonly TrainerId[] = ["tavi", "remy1", "pella", "brin", "ilex", "sable", "vell", "rook", "mica", "milo", "nix",
  "remy2", "kade", "frida", "morrow", "quartz", "lexi", "cinder", "opal", "nami", "theo", "vex", "lune",
  "remy3", "juno", "ash", "cass", "kiri", "hana", "kole", "osric"];

/**
 * The next duel on the route: the first unbeaten one after the latest win (optional earlier stops stay
 * on MAP, and an added stop never sends an established save backwards), then any unbeaten one left.
 * With every duel won: this week's open encores (when `week` is known), then circuit repeat laps.
 */
export function nextDuel(state: SaveState & { week?: number }): TrainerId {
  const done = (id: TrainerId) => state.defeatedTrainers.includes(id);
  const latest = Math.max(-1, ...ROUTE.map((id, i) => (done(id) ? i : -1)));
  const open = ROUTE.slice(latest + 1).find((id) => !done(id)) ?? ROUTE.find((id) => !done(id));
  if (open) return open;
  const encore = ROUTE.find((id) => (ENCORE_IDS as readonly TrainerId[]).includes(id) && encoreOpen(TRAINERS[id], state));
  return encore ?? CIRCUIT_IDS[state.circuitRematches % CIRCUIT_IDS.length]!;
}

/** Public habitat levels and owned resources only; advice never draws RNG or edits a save. */
export function routePlan(state: SaveState & { week?: number }) {
  const cleared = state.defeatedTrainers.includes("vell");
  const beds: TrainingBed[] = [];
  for (const zone of Object.keys(ZONES) as ZoneId[]) {
    const split = GROWN_SPLIT[zone];
    if (split) {
      const young = bedBand(ZONE_BEDS[zone]?.[split.north] ?? []), grown = bedBand(ZONE_BEDS[zone]?.[split.south] ?? []);
      if (young) beds.push({ zone, region: "north", ...young });
      if (grown) beds.push({ zone, region: "south", ...grown });
      continue;
    }
    const base = bedBand(ZONES[zone].encounters);
    const veteran = cleared ? bedBand(VETERAN_ENCOUNTERS[zone] ?? []) : null;
    if (base) beds.push({ zone, region: veteran ? "north" : "all", ...base });
    if (veteran) beds.push({ zone, region: "south", ...veteran });
  }
  const lead = state.party.find(m => m.hp > 0);
  const ready = state.party.filter(m => m.hp >= maxHp(m) / 2 && m.moves.some(slot => slot.pp > 0 && (peekMove(slot.id)?.power ?? 0) > 0))
    .sort((a, b) => b.level - a.level)[0];
  const training = beds.filter(b => b.hi <= (ready?.level ?? 1) + 2)
    .sort((a, b) => b.lo - a.lo || b.hi - a.hi)[0] ?? beds.find(b => b.zone === "drippond")!;
  const local = band(encountersAt(state.x, state.y, cleared));
  const kettle = SPOTS.find(s => s.kind === "rest" && zoneAt(s.x, s.y) === training.zone)!;
  const underlevelled = !!lead && !!local && lead.level + 2 < local.lo;
  const advice = !ready ? "Rest: low HP or attack PP."
    : underlevelled ? "Lead is below these wilds."
    : lead && ready.uid !== lead.uid && ready.level >= lead.level + 3 ? "A stronger lead can help."
    : "Rest often; check type matchups.";
  const nextId = nextDuel(state);
  const next = trainerEncounter(TRAINERS[nextId], state);
  const practice = cleared && !state.defeatedTrainers.includes("nix")
    ? [...CIRCUIT_IDS].reverse().find(id => state.defeatedTrainers.includes(id)) : undefined;
  return { lead, ready, training, local, kettle, underlevelled, advice, next, practice,
    repeat: state.defeatedTrainers.includes(nextId), zone: zoneAt(state.x, state.y) };
}
